import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import apiService from '../../services/api'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import {
  HiOutlineUserPlus,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlineClock,
  HiArrowPath,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import {
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Spinner,
} from '../../components/ui'

const ROLE_TONE = { admin: 'danger', diamond: 'info', gold: 'warning', silver: 'neutral' }
const STATUS_TONE = { accepted: 'success', rejected: 'danger', pending: 'warning' }

export default function ConnectionRequests({ type = 'received' }) {
  const { t } = useTranslation()
  const { getEntity } = useWorkspace()
  const connectionTerm = getEntity('connection')
  const employeeTerm = getEntity('employee')
  const caseTerm = getEntity('case')
  const documentTerm = getEntity('document')

  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedRequest, setExpandedRequest] = useState(null)
  const [processingId, setProcessingId] = useState(null)
  const [selectedRoles, setSelectedRoles] = useState({})
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  useEffect(() => {
    fetchConnectionRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  async function fetchConnectionRequests() {
    try {
      setLoading(true); setError(null)
      const endpoint = type === 'received' ? '/connection-requests/received/' : '/connection-requests/sent/'
      const response = await apiService.get(endpoint)
      if (response.success) {
        const all = response.data.data || []
        setRequests(type === 'received' ? all.filter(r => r.status === 'pending') : all)
      } else {
        // TODO: needs i18n key
        setError(response.error?.message || 'Failed to load connection requests')
      }
    } catch {
      // TODO: needs i18n key
      setError('Failed to load connection requests')
    } finally {
      setLoading(false)
    }
  }

  async function handleResponse(requestId, status, assignedRole = null) {
    try {
      setProcessingId(requestId)
      setConfirmDialog({ isOpen: false })
      const payload = { id: requestId, status }
      if (status === 'accepted' && assignedRole) payload.assigned_role = assignedRole
      const response = await apiService.put('/respond-connection-request/', payload)
      if (response.success) {
        setRequests(prev => prev.filter(r => r.id !== requestId))
        setExpandedRequest(null)
      } else {
        // TODO: needs i18n key
        alert(response.error?.message || 'Failed to respond to connection request')
      }
    } catch {
      // TODO: needs i18n key
      alert('Failed to respond to connection request')
    } finally {
      setProcessingId(null)
    }
  }

  const confirmResponse = (requestId, status, assignedRole, senderName) => {
    const isAccept = status === 'accepted'
    setConfirmDialog({
      isOpen: true,
      title: isAccept
        ? t('connections.requests.acceptTitle')
        : t('connections.requests.rejectTitle'),
      message: isAccept
        ? t('connections.requests.confirmAcceptMessage', {
            name: senderName,
            role: (assignedRole || '').toUpperCase(),
          })
        : t('connections.requests.confirmRejectMessage', { name: senderName }),
      type: isAccept ? 'success' : 'danger',
      confirmText: isAccept ? t('connections.requests.acceptWithRole') : t('connections.requests.reject'),
      onConfirm: () => handleResponse(requestId, status, assignedRole),
    })
  }

  const toggleExpanded = (id) => setExpandedRequest(expandedRequest === id ? null : id)

  const formatDate = (s) => {
    try {
      return new Date(s).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    } catch { return '—' }
  }

  // TODO: needs i18n keys for individual role descriptions (admin/diamond/gold/silver desc strings)
  const ROLES = [
    { value: 'admin',   label: 'Admin',   desc: 'Full access to all resources' },
    { value: 'diamond', label: 'Diamond', desc: `Access to all ${caseTerm.plural.toLowerCase()} and ${documentTerm.plural.toLowerCase()}` },
    { value: 'gold',    label: 'Gold',    desc: 'Moderate access with limited writes' },
    { value: 'silver',  label: 'Silver',  desc: 'Basic read-only access' },
  ]

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
        ))}
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (error) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineUserPlus}
          title={t('connections.list.couldntLoad')}
          description={error}
          action={<Button onClick={fetchConnectionRequests}>{t('common.retry')}</Button>}
        />
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-ink-primary">
          {type === 'received' ? t('connections.requests.received') : t('connections.requests.sent')}
        </h3>
        <Button variant="link" size="sm" leadingIcon={HiArrowPath} onClick={fetchConnectionRequests}>
          {t('common.refresh')}
        </Button>
      </div>

      {requests.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={HiOutlineUserPlus}
            title={type === 'received' ? t('connections.requests.noPending') : t('connections.requests.noSent')}
            description={
              type === 'received'
                ? t('connections.requests.noPendingHelp', {
                    conn: connectionTerm.singular.toLowerCase(),
                    employee: employeeTerm.plural.toLowerCase(),
                  })
                : t('connections.requests.noSentHelp', { conn: connectionTerm.singular })
            }
            compact
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {requests.map((request) => {
            const isExpanded = expandedRequest === request.id
            const isProcessing = processingId === request.id
            const personName = type === 'received' ? request.sender_name : request.receiver_name
            return (
              <li key={request.id}>
                <Card padding="none" className="relative overflow-hidden">
                  <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar name={personName} size="md" />
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-ink-primary truncate">{personName}</h4>
                        <div className="inline-flex items-center gap-1 text-xs text-ink-muted mt-1">
                          <HiOutlineClock className="w-3 h-3" />
                          {t('connections.requests.sentAt', { date: formatDate(request.created_at) })}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {type === 'received' ? (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={HiOutlineXMark}
                            disabled={isProcessing}
                            onClick={() => confirmResponse(request.id, 'rejected', null, personName)}
                          >
                            {t('connections.requests.reject')}
                          </Button>
                          <Button
                            size="sm"
                            leadingIcon={HiOutlineCheck}
                            disabled={isProcessing}
                            onClick={() => toggleExpanded(request.id)}
                          >
                            {t('connections.requests.selectRole')}
                          </Button>
                        </>
                      ) : (
                        <Badge tone={STATUS_TONE[request.status] || 'neutral'}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {isExpanded && type === 'received' && (
                    <div className="px-4 pb-4 border-t border-border-subtle">
                      <div className="pt-4">
                        <h5 className="text-sm font-semibold text-ink-primary mb-3">
                          {t('connections.requests.assignRoleTo', { name: personName })}
                        </h5>
                        <div className="space-y-2 mb-4">
                          {ROLES.map((role) => (
                            <label
                              key={role.value}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedRoles[request.id] === role.value
                                  ? 'border-brand bg-brand-soft'
                                  : 'border-border hover:border-border-strong'
                              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <input
                                type="radio"
                                name={`role-request-${request.id}`}
                                value={role.value}
                                checked={selectedRoles[request.id] === role.value}
                                onChange={(e) => !isProcessing && setSelectedRoles(prev => ({ ...prev, [request.id]: e.target.value }))}
                                disabled={isProcessing}
                                className="h-4 w-4 text-brand border-border-strong focus:ring-brand/30 focus:ring-2"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <Badge tone={ROLE_TONE[role.value] || 'neutral'} size="sm">{role.label}</Badge>
                                </div>
                                <p className="text-xs text-ink-muted">{role.desc}</p>
                              </div>
                            </label>
                          ))}
                        </div>

                        <div className="flex items-end justify-between gap-3 flex-wrap">
                          <Banner tone="info" className="flex-1 min-w-[200px]">
                            {t('connections.requests.noteText', { conns: connectionTerm.plural })}
                          </Banner>
                          <Button
                            size="sm"
                            leadingIcon={HiOutlineCheck}
                            disabled={isProcessing || !selectedRoles[request.id]}
                            onClick={() => {
                              const role = selectedRoles[request.id]
                              if (role) confirmResponse(request.id, 'accepted', role, personName)
                            }}
                          >
                            {t('connections.requests.acceptWithRole')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {isProcessing && (
                    <div className="absolute inset-0 bg-surface/70 backdrop-blur-sm flex items-center justify-center">
                      <div className="inline-flex items-center gap-2">
                        <Spinner size="sm" />
                        <span className="text-sm text-ink-secondary">{t('connections.requests.processing')}</span>
                      </div>
                    </div>
                  )}
                </Card>
              </li>
            )
          })}
        </ul>
      )}

      <Banner tone="warning" title={t('connections.requests.guidelinesTitle', { conn: connectionTerm.singular })}>
        <ul className="text-sm space-y-1 list-disc list-inside ms-1">
          <li>{t('connections.requests.guideline1')}</li>
          <li>{t('connections.requests.guideline2')}</li>
          <li>{t('connections.requests.guideline3')}</li>
          <li>{t('connections.requests.guideline4')}</li>
        </ul>
      </Banner>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}
